import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Typography,
  LinearProgress,
  Box,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon as MenuItemIcon,
  CircularProgress,
  IconButton,
  Stack,
  Divider,
  Drawer,
  ListItemIcon,
  Tooltip,
} from "@mui/material";
import {
  CloudUpload,
  Refresh,
  CheckCircle,
  Error,
  ArrowDropDown,
  InsertDriveFile,
  FolderOpen,
  Visibility,
  Close as CloseIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { uploadFiles, updateIndex } from "../../api/agentApi";
import { v4 as uuidv4 } from 'uuid';
import { UI_RADIUS } from "../../constants";

const steps = ["Upload Files", "Update Collection"];

interface FileWithStatus {
  file: File;
  status: "pending" | "uploading" | "success" | "error";
  error?: string;
  relativePath?: string;
  id: string;
}

interface UploadResult {
  uploaded: Array<{
    filename: string;
    path: string;
    size: number;
  }>;
  count: number;
  errors?: string[];
}

interface UpdateResult {
  status: string;
  message: string;
  updated_fields?: Record<string, any>;
}

interface SourcePath {
  path: string;
  status?: string;
}

interface IndexDetails {
  source_path: Array<SourcePath | string>;
  name: string;
  type: string;
  status: string;
}

interface UpdateIndexDrawerProps {
  open: boolean;
  onClose: () => void;
  indexId: string;
  indexName?: string;
  indexDetails?: IndexDetails;
  uploadId: string; // Upload ID from index creation - REQUIRED
}

export default function UpdateIndexDrawer({
  open,
  onClose,
  indexId,
  indexName,
  indexDetails,
  uploadId: providedUploadId,
}: UpdateIndexDrawerProps) {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedFiles, setSelectedFiles] = useState<FileWithStatus[]>([]);
  const [uploadResults, setUploadResults] = useState<UploadResult | null>(null);
  const [updateResults, setUpdateResults] = useState<UpdateResult | null>(null);
  const [error, setError] = useState<string>("");
  const [autoStarted, setAutoStarted] = useState<boolean>(false);
  const [newUploadPath, setNewUploadPath] = useState<string>("");
  // Track upload ID to reuse for same index across multiple upload sessions
  const [uploadId, setUploadId] = useState<string>("");

  // New states for file/folder selection
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [loadingFiles, setLoadingFiles] = useState<boolean>(false);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const cancellationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Utility functions from RAGFileUploader
  const generateFileId = (file: File): string =>
    `${file.name}-${file.size}-${Date.now()}`;

  const clearCancellationTimeout = (): void => {
    if (cancellationTimeoutRef.current) {
      clearTimeout(cancellationTimeoutRef.current);
      cancellationTimeoutRef.current = null;
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setAnchorEl(null);
  };

  const handleSelectFiles = (): void => {
    handleMenuClose();
    setLoadingFiles(true);
    clearCancellationTimeout();

    // Timeout to handle cancel
    cancellationTimeoutRef.current = setTimeout(() => {
      setLoadingFiles(false);
      cancellationTimeoutRef.current = null;
    }, 500);

    fileInputRef.current?.click();
  };

  const handleSelectFolder = (): void => {
    handleMenuClose();
    setLoadingFiles(true);
    clearCancellationTimeout();

    // Timeout to handle cancel
    cancellationTimeoutRef.current = setTimeout(() => {
      setLoadingFiles(false);
      cancellationTimeoutRef.current = null;
    }, 500);

    folderInputRef.current?.click();
  };

  const handleFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    // Clear the cancellation timeout immediately because selection was successful
    clearCancellationTimeout();
    setLoadingFiles(false);

    const selectedFilesList = event.target.files;
    if (!selectedFilesList || selectedFilesList.length === 0) {
      event.target.value = "";
      return;
    }

    // Convert FileList to array with relative paths
    const filesArray: FileWithStatus[] = Array.from(selectedFilesList).map(
      (file) => ({
        file,
        status: "pending" as const,
        relativePath: file.name, // For individual files, relativePath is just the filename
        id: generateFileId(file),
      })
    );

    setSelectedFiles(filesArray);
    // Reset autoStarted flag to allow new upload process
    setAutoStarted(false);
    // Reset the input so the same files can be selected again
    event.target.value = "";
  };

  const handleFolderSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    // Clear the cancellation timeout immediately because selection was successful
    clearCancellationTimeout();
    setLoadingFiles(false);

    const selectedFilesList = event.target.files;
    if (!selectedFilesList || selectedFilesList.length === 0) {
      event.target.value = "";
      return;
    }

    // Convert FileList to array with webkit relative paths for folders
    const filesArray: FileWithStatus[] = Array.from(selectedFilesList).map(
      (file) => {
        const webkitPath = (file as any).webkitRelativePath || file.name;
        return {
          file,
          status: "pending" as const,
          relativePath: webkitPath,
          id: generateFileId(file),
        };
      }
    );

    setSelectedFiles(filesArray);
    // Reset autoStarted flag to allow new upload process
    setAutoStarted(false);
    // Reset the input so the same folder can be selected again
    event.target.value = "";
  };

  const resetDrawer = (): void => {
    setActiveStep(0);
    setLoading(false);
    setSelectedFiles([]);
    setUploadResults(null);
    setUpdateResults(null);
    setError("");
    setAutoStarted(false);
    setLoadingFiles(false);
    setDrawerOpen(false);
    setNewUploadPath("");
    clearCancellationTimeout();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (folderInputRef.current) {
      folderInputRef.current.value = "";
    }
  };

  const handleClear = (): void => {
    setSelectedFiles([]);
    setUploadResults(null);
    setError("");
    setDrawerOpen(false);
    setNewUploadPath("");
  };

  // Helper function to get display path (kept for potential future use)
  const getDisplayPath = (): string => {
    if (newUploadPath) {
      return newUploadPath;
    }
    return "Upload path will be generated automatically";
  };

  // Helper to get files with final status for drawer display
  const filesWithFinalStatus = selectedFiles.filter(
    (f) => f.status === "success" || f.status === "error"
  );

  const handleClose = (): void => {
    resetDrawer();
    onClose();
  };

  // Initialize upload ID when drawer opens for an index
  useEffect(() => {
    if (open && indexId) {
      // Use provided uploadId from props (always required now)
      if (providedUploadId && !uploadId) {
        setUploadId(providedUploadId);
      }
      // If uploadId already exists in state, reuse it (same index, multiple drawer opens)
    }
  }, [open, indexId, providedUploadId]);

  // Reset upload ID when switching to a different index
  useEffect(() => {
    setUploadId("");
  }, [indexId]);

  // Auto-start the process when files are selected and drawer is open
  useEffect(() => {
    if (selectedFiles.length > 0 && open && !autoStarted && !loading) {
      setAutoStarted(true);
      startUpdateProcess();
    }
  }, [selectedFiles, open, autoStarted, loading]);

  const startUpdateProcess = async (): Promise<void> => {
    if (selectedFiles.length === 0) {
      setError("Please select files to upload");
      return;
    }

    if (
      !indexDetails ||
      !indexDetails.source_path ||
      indexDetails.source_path.length === 0
    ) {
      setError("Index details not available or no source paths found");
      return;
    }

    setLoading(true);
    setError("");
    setActiveStep(0);
    
    // Reset upload path for new upload
    setNewUploadPath("");

    // Update file statuses to uploading
          setSelectedFiles((prev) =>
      prev.map((fileItem) => ({ ...fileItem, status: "uploading" as const }))
    );

    try {
      // Use the stored upload ID for this index (reused across multiple uploads)
      // uploadId is set when drawer opens via useEffect
      
      // Step 1: Upload Files with Batching
      const batchSize = 900;
      const batches = [];

      // Split files into batches
      for (let i = 0; i < selectedFiles.length; i += batchSize) {
        batches.push(selectedFiles.slice(i, i + batchSize));
      }

      let allUploaded: Array<{ filename: string; path: string; size: number }> = [];
      let allErrors: string[] = [];

      // Process each batch
      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        const formData = new FormData();
        
        // Add upload_id (similar to RAGFileUploader)
        formData.append("upload_id", uploadId);
        
        batch.forEach(({ file, relativePath }) => {
          formData.append("files", file);
          if (relativePath && relativePath !== file.name) {
            formData.append("paths", relativePath);
          }
        });

        try {
          const results = await uploadFiles(formData);
          
          // Set the upload path from the first uploaded file (backend computes the path)
          if (results.uploaded && results.uploaded.length > 0) {
            const firstUploadPath = results.uploaded[0].path;
            const lastSlashIndex = firstUploadPath.lastIndexOf("/");
            const computedPath = lastSlashIndex > 0 
              ? firstUploadPath.substring(0, lastSlashIndex) 
              : firstUploadPath;
            setNewUploadPath(computedPath);
          }
          
          // Combine results from this batch
          allUploaded = allUploaded.concat(results.uploaded || []);
          allErrors = allErrors.concat(results.errors || []);

          // Update file statuses for this batch
          setSelectedFiles((prev) =>
            prev.map((fileItem) => {
              // Only update files in this batch
              if (!batch.find((b) => b.id === fileItem.id)) return fileItem;
              
              const uploaded = results.uploaded.find(
                (u: any) => u.filename === fileItem.file.name
              );
              const hasError = results.errors?.some(
                (err: string) =>
                  err.includes(fileItem.file.name) ||
                  err.includes(fileItem.relativePath || "")
              );

              if (uploaded) {
                return {
                  ...fileItem,
                  status: "success" as const,
                  error: undefined,
                };
              } else if (hasError) {
                return {
                  ...fileItem,
                  status: "error" as const,
                  error: "Upload failed: server error",
                };
              } else {
                return {
                  ...fileItem,
                  status: "error" as const,
                  error: "Upload failed",
                };
              }
            })
          );
        } catch (batchErr: any) {
          // Handle batch-specific errors
          const batchErrorMsg = batchErr?.message || "Batch upload failed";
          allErrors.push(batchErrorMsg);
          
          // Mark all files in this batch as error
          setSelectedFiles((prev) =>
            prev.map((fileItem) => {
              if (!batch.find((b) => b.id === fileItem.id)) return fileItem;
              return {
                ...fileItem,
                status: "error" as const,
                error: "Batch upload failure",
              };
            })
          );
        }
      }

      // Set combined upload results
      const combinedResults = {
        uploaded: allUploaded,
        count: allUploaded.length,
        errors: allErrors.length > 0 ? allErrors : undefined,
      };
      setUploadResults(combinedResults);

      setActiveStep(1);

      // Step 2: Update Index
      // Extract the paths where files were uploaded
      const uploadPaths = allUploaded.map((file: any) => file.path);

      // Get unique directory paths from uploaded files
      const uniqueUploadPathsSet = new Set(
        uploadPaths.map((filePath: string) => {
          const lastSlashIndex = filePath.lastIndexOf("/");
          return lastSlashIndex > 0
            ? filePath.substring(0, lastSlashIndex)
            : filePath;
        })
      );
      const uniqueUploadPaths = Array.from(uniqueUploadPathsSet);

      // Get existing paths from index details
      const existingPaths = indexDetails.source_path.map((pathItem) =>
        typeof pathItem === "object" ? pathItem.path : pathItem
      );

      // Combine existing paths with new upload paths
      const allPaths = [...existingPaths, ...uniqueUploadPaths];
      const uniqueAllPaths = Array.from(new Set(allPaths));

      const updateData = {
        source_path: uniqueAllPaths,
      };

      const updateResults = await updateIndex(indexId, updateData);
      setUpdateResults(updateResults);
      setActiveStep(2); // Complete
    } catch (err: any) {
      const errorMessage = err?.message || "Update failed: Unknown error";
      setError(errorMessage);
      // Mark all files as error on critical failure
      setSelectedFiles((prev) =>
        prev.map((fileItem) => ({
          ...fileItem,
          status: "error" as const,
          error: "Critical upload failure",
        }))
      );
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step: number): React.ReactNode => {
    switch (step) {
      case 0:
        return (
          <Box>
            {uploadResults && (
              <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
                {uploadResults.errors && uploadResults.errors.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {uploadResults.errors.join(", ")}
                  </Alert>
                )}
              </Paper>
            )}
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Triggering index update to process the newly uploaded files...
            </Typography>
            {updateResults && (
              <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle2">Update Results:</Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> {updateResults.status}
                </Typography>
                <Typography variant="body2">
                  <strong>Message:</strong> {updateResults.message}
                </Typography>
                {updateResults.updated_fields &&
                  Object.keys(updateResults.updated_fields).length > 0 && (
                    <Typography variant="body2">
                      <strong>Updated Fields:</strong>{" "}
                      {Object.keys(updateResults.updated_fields).join(", ")}
                    </Typography>
                  )}
              </Paper>
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  const resetContent = () => {
    // resetDrawer();
    return <></>;
  };
  /* Success/completion Alert with Drawer */
  const getFileUploadAlert = () => {
    return (
      <>
        {uploadResults && (
          <Alert
            severity={
              uploadResults?.count === selectedFiles.length &&
              !(uploadResults.errors && uploadResults?.errors.length)
                ? "success"
                : "warning"
            }
            sx={{ mt: 2 }}
            action={
              <>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setDrawerOpen(true)}
                  startIcon={<Visibility />}
                >
                  View Status ({filesWithFinalStatus.length})
                </Button>
                <IconButton onClick={handleClear} sx={{ ml: 1 }}>
                  <CloseIcon />
                </IconButton>
              </>
            }
          >
            {uploadResults?.count === selectedFiles.length &&
            !(uploadResults.errors && uploadResults.errors.length) ? (
              <>
                Upload complete. Successfully uploaded all {uploadResults.count}{" "}
                file(s).
              </>
            ) : (
              <>
                Upload complete. Uploaded {uploadResults?.count} out of{" "}
                {selectedFiles.length} file(s).
                {uploadResults?.errors && uploadResults?.errors?.length > 0 && (
                  <>
                    <br />
                    {uploadResults.errors.length} error(s) occurred. See details
                    in the drawer.
                  </>
                )}
              </>
            )}
          </Alert>
        )}
      </>
    );
  };

  const isComplete = activeStep >= steps.length;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", md: 900 },
          height: "100%",
          maxWidth: "100vw",
          maxHeight: "100vh",
          pt: 0,
        },
      }}
    >
      <Box
        sx={{ display: "flex", flexDirection: "column", height: "95%", pt: 12 }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            px: 3,
            pt: 2,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Update Index: {indexName || indexId}
          </Typography>
          <Tooltip title="Close Drawer">
            <IconButton onClick={handleClose} size="large">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Content Area */}
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            px: 3,
            pb: 3,
            overflow: "auto",
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Always show file/folder selection button */}
          <Box sx={{ py: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
              <InfoOutlinedIcon fontSize="medium" color="info" />
              <Typography variant="caption" color="text.secondary">
                Select files or folder to add to the index
              </Typography>
            </Box>

            {/* File/Folder selection button with dropdown */}
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              endIcon={<ArrowDropDown />}
              onClick={handleMenuClick}
              disabled={loading || loadingFiles}
              sx={{ mt: 2, borderRadius: UI_RADIUS.button, textTransform: "none" }}
              size="large"
            >
              {loadingFiles ? "Loading files..." : "Upload Files/Folder and Index"}
            </Button>
            <Box sx={{ pt: 2 }}>
              <Typography variant="caption" color="textSecondary" gutterBottom>
                {newUploadPath 
                  ? `Files uploaded to: ${newUploadPath}`
                  : "Files will be uploaded to the index path"
                }
              </Typography>
            </Box>
            {/* Dropdown menu */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleSelectFiles}>
                <MenuItemIcon>
                  <InsertDriveFile fontSize="small" />
                </MenuItemIcon>
                <Typography>Upload Files</Typography>
              </MenuItem>
              <MenuItem onClick={handleSelectFolder}>
                <MenuItemIcon>
                  <FolderOpen fontSize="small" />
                </MenuItemIcon>
                <Typography>Upload Folder</Typography>
              </MenuItem>
            </Menu>

            {/* Hidden file inputs */}
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              onChange={handleFileSelect}
            />
            <input
              ref={folderInputRef}
              type="file"
              hidden
              // @ts-ignore - webkitdirectory is not in the TypeScript DOM types
              webkitdirectory="true"
              directory="true"
              multiple
              onChange={handleFolderSelect}
            />

            <Divider sx={{ my: 3 }} />
            {/* Loading indicator while browser processes files */}
            {loadingFiles && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: 2,
                }}
              >
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography variant="body1">Loading files...</Typography>
              </Box>
            )}
          </Box>

          {/* Progress and status sections below the button */}
          {selectedFiles.length > 0 && !isComplete && (
            <>
              {loading && (
                <Box sx={{ mb: 2 }}>
                  <LinearProgress />
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 1 }}
                  >
                    {activeStep === 0
                      ? "Uploading files..."
                      : "Updating index..."}
                  </Typography>
                </Box>
              )}

              <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((label, index) => (
                  <Step key={label} expanded={true}>
                    <StepLabel>
                      {label}
                      {index < activeStep && (
                        <CheckCircle
                          color="success"
                          sx={{ ml: 1, fontSize: 20 }}
                        />
                      )}
                    </StepLabel>
                    <StepContent>{getStepContent(index)}</StepContent>
                  </Step>
                ))}
              </Stepper>
            </>
          )}

          {selectedFiles.length > 0 && isComplete && (
            <>
              {/* Show completed stepper with all step details */}
              <Stepper
                activeStep={steps.length}
                orientation="vertical"
                sx={{ mb: 3 }}
              >
                {steps.map((label, index) => (
                  <Step key={label} completed={true} expanded={true}>
                    <StepLabel>
                      {label}
                      <CheckCircle
                        color="success"
                        sx={{ ml: 1, fontSize: 20 }}
                      />
                    </StepLabel>
                    <StepContent>
                      {index === 0 && !loadingFiles && getFileUploadAlert()}
                      {index === 1 && resetContent()}
                    </StepContent>
                  </Step>
                ))}
              </Stepper>

              {/* Completion status at bottom */}
              <Box
                sx={{
                  // textAlign: 'center',
                  p: 2,
                  backgroundColor: "rgba(76, 175, 80, 0.1)",
                  borderRadius: 2,
                  border: "1px solid rgba(76, 175, 80, 0.3)",
                }}
              >
                <Stack direction="row" spacing={4}>
                  <CheckCircle color="success" sx={{ fontSize: 36, mb: 2 }} />
                  <Typography variant="h6" gutterBottom color="success.main">
                    Index Update With New Files Complete!
                  </Typography>
                </Stack>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Status Drawer - nested drawer for file status details */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: { width: { xs: "100%", sm: 450 } },
        }}
      >
        <Box sx={{ pt: 12, px: 2, pb: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6">Upload Status Details</Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <ChevronRightIcon />
            </IconButton>
          </Stack>
          <Divider sx={{ mb: 2 }} />

          <List dense>
            {filesWithFinalStatus.length > 0 ? (
              filesWithFinalStatus.map((fileItem: FileWithStatus) => (
                <ListItem
                  key={fileItem.id}
                  sx={{
                    borderRadius: 1,
                  }}
                >
                  <ListItemIcon>
                    {fileItem.status === "success" ? (
                      <CheckCircle color="success" />
                    ) : (
                      <Error color="error" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={fileItem.file.name}
                    secondary={
                      <Stack direction="column" spacing={0.5}>
                        <Typography variant="caption" display="block">
                          Status: {fileItem.status.toUpperCase()}
                        </Typography>
                        {fileItem.relativePath && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Path: {fileItem.relativePath}
                          </Typography>
                        )}
                        {fileItem.error && (
                          <Chip
                            label={fileItem.error}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ width: "fit-content", mt: 0.5 }}
                          />
                        )}
                      </Stack>
                    }
                  />
                </ListItem>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No final statuses to display yet. Start an upload
              </Typography>
            )}
          </List>
        </Box>
      </Drawer>
    </Drawer>
  );
}
